import { useCallback, useEffect, useRef, useState } from "react";
import { Bell, CheckCircle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "hace un momento";
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  return `hace ${Math.floor(diff / 86400)} d`;
}

export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [pending, setPending] = useState(0);
  const ref = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/notifications").then((r) => r.data);
      setItems(r.items || []);
      setPending(r.pending || 0);
    } catch {
      /* silencioso: usuario nuevo aún sin notificaciones */
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 45000);
    return () => clearInterval(id);
  }, [load]);

  // Cerrar al hacer click afuera
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      setPending((p) => Math.max(0, p - 1));
    } catch { /* ignore */ }
  };

  const markAll = async () => {
    if (pending === 0) return;
    try {
      await api.post("/notifications/read-all");
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setPending(0);
    } catch { /* ignore */ }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="notifications-bell"
        aria-label="Notificaciones"
        className="relative p-2 rounded-md text-foreground/70 hover:bg-accent hover:text-foreground transition-colors"
      >
        <Bell size={20} weight="duotone" />
        {pending > 0 && (
          <span
            data-testid="notifications-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold leading-none"
          >
            {pending > 99 ? "99+" : pending}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notifications-panel"
          className="absolute right-0 mt-2 w-[360px] max-h-[480px] overflow-hidden bg-card border border-border rounded-md shadow-lg z-50 flex flex-col animate-fade-in"
        >
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <div>
              <div className="font-heading font-semibold text-sm">Notificaciones</div>
              <div className="text-[11px] text-muted-foreground">
                {pending > 0 ? `${pending} sin leer` : "Estás al día"}
              </div>
            </div>
            {pending > 0 && (
              <button
                onClick={markAll}
                data-testid="notifications-mark-all"
                className="text-xs text-primary hover:underline font-medium"
              >
                Marcar todo
              </button>
            )}
          </div>

          <div className="overflow-y-auto flex-1">
            {items.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground" data-testid="notifications-empty">
                Aún no hay notificaciones.
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {items.map((n) => (
                  <li
                    key={n.id}
                    data-testid={`notification-item-${n.id}`}
                    className={cn(
                      "px-4 py-3 hover:bg-accent/40 transition-colors cursor-pointer",
                      !n.read && "bg-primary/5"
                    )}
                    onClick={() => !n.read && markRead(n.id)}
                  >
                    <div className="flex items-start gap-2">
                      {!n.read ? (
                        <span className="mt-1.5 w-2 h-2 rounded-full bg-primary shrink-0" />
                      ) : (
                        <CheckCircle size={14} weight="duotone" className="mt-0.5 text-muted-foreground shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className={cn("text-sm leading-tight", !n.read ? "font-medium" : "text-foreground/80")}>
                          {n.title}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1 leading-snug whitespace-pre-line">
                          {n.body}
                        </div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mt-1.5">
                          {timeAgo(n.created_at)}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
